import { DataManager } from 'utils/DataManager/dataManager';
import { DataState, EmployeeLevelEnum } from 'typings/enums';
import { IEmployeeDataManager } from 'typings/dataManagers';
import { ISearchedEmployee, IEmployee, IEmployeeCriteria, IEmployeeIdMap } from 'typings/employee';
import { EmployeeConfiguration } from 'nomst/Stores/Configuration/employeeConfiguration';
import { createManagerIdsUrl } from 'utils/stringUtils';
import { EmployeeCountrySummary } from 'nomst/Stores/Budget/employeeCountrySummary';
import { IEmployeeErrorData, IJob, ILabEmployee, IUpdateLabEmployeeResult } from 'typings/employeeErrorData';
import { IBaseEmployeeLevel } from 'typings/baseEmployeeLevel';
import { Employee } from 'nomst/Stores/Decision/employee';

class EmployeeDataManager extends DataManager implements IEmployeeDataManager {

    private _baseUrl: string;

    async getPublicEmployeeByUserId(masterAccountId: string, userId: string): Promise<Nullable<IEmployee>> {
        const receive = () => this._getDataRequest<IEmployee>(
            `${this.baseUrl}/api/accounts/${masterAccountId}/employees/public/user?userId=${userId}`);
        return await this.getData<IEmployee>(receive, false);
    }

    async getConfiguration(masterAccountId: string): Promise<EmployeeConfiguration> {
        const receive = () => this._getDataRequest<IValueObject>(
            `${this.baseUrl}/api/accounts/${masterAccountId}/configuration`);
        const result = await this.getData<IValueObject>(receive, false);
        if (!result) {
            return {} as EmployeeConfiguration;
        }
        return this.parseConfiguration(result);
    }

    async ignoreUserError(masterAccountId: string, employeeId: string): Promise<void|null> {
        const receive = () => this._postDataRequest<string, void>(
            `/api/accounts/${masterAccountId}/team/ignoreEmployee`, employeeId);
        return await this.getData<void|null>(receive, true);
    }

    async saveConfiguration(masterAccountId: string, configuration: EmployeeConfiguration): Promise<void> {
        const configurationToService = this.composeConfiguration(configuration);
        const receive = () => this._postDataRequest<typeof configurationToService, void>(
            `${this.baseUrl}/api/accounts/${masterAccountId}/configuration`, configurationToService);
        await this.getData<void>(receive);
    }

    async getEmployeeCountrySummaries(
        masterAccountId: string, cycleId: number
    ): Promise<Nullable<EmployeeCountrySummary[]>> {
        const receive = () => this._getDataRequest<EmployeeCountrySummary[]>(
            `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/employees/countries`);
        return await this.getData<EmployeeCountrySummary[]>(receive, false);
    }

    async getEmployeeBasicInfo(employeeSourceId: string, masterAccountId: string): Promise<IEmployeeInfo> {
        const receive = () => this._getDataRequest<IEmployeeInfo>(
            // tslint:disable-next-line:max-line-length
            `/api/accounts/${masterAccountId}/team/employees?employeeSourceId=${encodeURIComponent(employeeSourceId)}`);
        const employee = await this.getData<IEmployeeInfo>(receive, false);

        return employee ? {
            managerDisplayName: employee.managerDisplayName,
            workEmail: employee.workEmail,
            department: employee.department,
            fullTimeEquivalent: employee.fullTimeEquivalent,
            grade: employee.grade
        } : {} as IEmployeeInfo;
    }

    async getSuggestedEmployees(
        masterAccountId: string,
        managerSourceIds: string[],
        isDirectOnly: boolean,
        searchText: string,
        resultLimit: number): Promise<Nullable<ISearchedEmployee[]>> {
        const managerIds = createManagerIdsUrl(managerSourceIds);
        const option = isDirectOnly ?
            `?maxLevel=1&searchText=${searchText}&resultLimit=${resultLimit}${managerIds}` :
            `?searchText=${searchText}&resultLimit=${resultLimit}${managerIds}`;

        const receive = () => this._getDataRequest<ISearchedEmployee[]>(
            `${this.baseUrl}/api/accounts/${masterAccountId}/managers/employees/public/suggested${option}`);
        return await this.getData<ISearchedEmployee[]>(receive, false);
    }

    async saveEmpBonusFieldDecision(
        masterAccountId: string,
        employeeSourceId: string,
        cycleId: number,
        budgetCycleId: number,
        bonusFieldDecisions: IBonusFieldRule[]): Promise<void> {

        const receive = () => this._postDataRequest<typeof bonusFieldDecisions, void>(
            // tslint:disable-next-line:max-line-length
            `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/budget/${budgetCycleId}/employee/${employeeSourceId}/BonusFieldDecision`,
            bonusFieldDecisions);

        await this.getData<void>(receive, true);
    }

    async getEmployeeLastEligibleBudget(
        masterAccountId: string,
        cycleId: number,
        employeeSourceId: string,
        budgetCycleid: number
    ): Promise<Nullable<number>> {
        const receive = () =>
            this._getDataRequest<number>(
                // tslint:disable-next-line:max-line-length
                `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/employees/budgets/${budgetCycleid}/lasteligible?employeeSourceId=${encodeURIComponent(employeeSourceId)}`);
        return await this.getData<number>(receive, false);
    }

    async getPublicEmployees(masterAccountId: string, criteria: IEmployeeCriteria): Promise<Nullable<IEmployee[]>> {
        const receive = () => this._postDataRequest<typeof criteria, IEmployee[]>(
            `${this.baseUrl}/api/accounts/${masterAccountId}/employees/public`, criteria);
        return await this.getData<IEmployee[]>(receive, true);
    }

    async getTopEmployeeIds(masterAccountId: string): Promise<Nullable<string[]>> {
        const receive = () => this._getDataRequest<string[]>(
            `${this.baseUrl}/api/accounts/${masterAccountId}/employees/public/top`);
        return await this.getData<string[]>(receive, false);
    }

    async getEmployeeErrorData(masterAccountId: string, blockUi: boolean = false): Promise<Nullable<IEmployeeErrorData[]>> {
        const receive = () => this._getDataRequest<IEmployeeErrorData[]>(
            `/api/accounts/${masterAccountId}/team/invalidEmployees`);
        return await this.getData<IEmployeeErrorData[]>(receive, blockUi);
    }

    async getDeletedEmployees(masterAccountId: string, blockUi: boolean = false): Promise<Nullable<IEmployee[]>> {
        const receive = () => this._getDataRequest<IEmployee[]>(
            `/api/accounts/${masterAccountId}/team/deletedEmployees`);
        return await this.getData<IEmployee[]>(receive, blockUi);
    }

    async getEmployeeIdsOfUsers(masterAccountId: string, blockUi: boolean = false): Promise<Nullable<string[]>> {
        const receive = () => this._getDataRequest<string[]>(
            `/api/accounts/${masterAccountId}/team/employeeUsers`);
        return await this.getData<string[]>(receive, blockUi);
    }

    async getJobData(masterAccountId: string): Promise<Nullable<IJob[]>> {
        const receive = () => this._getDataRequest<IJob[]>(
            `/api/accounts/${masterAccountId}/team/jobs`);
        return await this.getData<IJob[]>(receive, true);
    }

    async getEmployeeIds(masterAccountId: string): Promise<Nullable<IEmployeeIdMap>> {
        const receive = () => this._getDataRequest<IEmployeeIdMap>(
            `/api/accounts/${masterAccountId}/team/employeeIds`);
        return await this.getData<IEmployeeIdMap>(receive, true);
    }

    async updateLabEmployee(masterAccountId: string, labEmployee: Nullable<ILabEmployee>): Promise<Nullable<IUpdateLabEmployeeResult>> {
        const receive = () => this._postDataRequest<typeof labEmployee, void>(
            `/api/accounts/${masterAccountId}/team/labEmployee/update`, labEmployee);
        return await this.getData<IUpdateLabEmployeeResult>(receive, true);
    }

    async getEmployeeWorkLocations(masterAccountId: string): Promise<string[]> {
        const receive = () => this._getDataRequest<string[]>(
            `${this.baseUrl}/api/accounts/${masterAccountId}/employees/workLocations`);
        const result = await this.getData<string[]>(receive, false);

        return result || [];
    }

    async getEmployeeDepartments(masterAccountId: string): Promise<string[]> {
        const receive = () => this._getDataRequest<string[]>(
            `${this.baseUrl}/api/accounts/${masterAccountId}/workflow/department`);
        const result = await this.getData<string[]>(receive, false);

        return result || [];
    }

    async getEmployeeBusinessUnits(masterAccountId: string): Promise<IBaseEmployeeLevel[]> {
        const receive = () => this._getDataRequest<string[]>(
            `${this.baseUrl}/api/accounts/${masterAccountId}/workflow/businessunit`);
        const result = await this.getData<string[]>(receive, false);

        return (result || []).map(businessUnitName => ({
            level: EmployeeLevelEnum.BusinessUnit,
            levelName: businessUnitName
        } as IBaseEmployeeLevel));
    }

    async getEmployeeDivisions(masterAccountId: string): Promise<Nullable<IBaseEmployeeLevel[]>> {
        const receive = () => this._getDataRequest<string[]>(
            `${this.baseUrl}/api/accounts/${masterAccountId}/workflow/division`);
        const result = await this.getData<string[]>(receive, false);

        return (result || []).map(divisionName => ({
            level: EmployeeLevelEnum.Division,
            levelName: divisionName
        } as IBaseEmployeeLevel));
    }

    async getEmployeeJobCollabList(masterAccountId: string): Promise<Employee[]> {
        const receive = () => this._getDataRequest<Employee[]>(
            `/api/accounts/${masterAccountId}/team/employeeList?directOnly=true`);
        const result = await this.getData<Employee[]>(receive, true);
        return result ?? [];
    }

    async getEmployeeDetails(masterAccountId: string, selectedEmployeeSourceId: string): Promise<Nullable<Employee>> {
        const receive = () => this._getDataRequest<Employee[]>(
            `/api/accounts/${masterAccountId}/team/employeeDetails/${selectedEmployeeSourceId}`);
        const result = await this.getData<Employee>(receive, true);
        return result ?? null;
    }

    setDataState(state: DataState): void {
        this.protectedTransitionDataState(state);
    }

    setBaseUrl(url: string): void {
        this._baseUrl = url;
    }

    get baseUrl(): string {
        return this._baseUrl;
    }

    private parseConfiguration(configurationFromService: IValueObject): EmployeeConfiguration {
        return JSON.parse(configurationFromService.value) as EmployeeConfiguration;
    }

    private composeConfiguration(configuration: EmployeeConfiguration): IValueObject {
        return {
            value: JSON.stringify(configuration)
        };
    }
}

export { EmployeeDataManager };
export default EmployeeDataManager;